import React, { useMemo, useState } from 'react';
import { GlobalStyle } from './GlobalStyles';
import { ThemeManager } from './theme/ThemeManager';

import { router } from './router';
import { ThemeProvider } from 'styled-components';
import { DarkTheme, LightTheme } from './theme/glueTheme';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

export const ThemeSwitchingContext = React.createContext<{ toggleTheme: () => void; activeTheme: 'LIGHT' | 'DARK' }>({
    toggleTheme: () => {},
    activeTheme: 'LIGHT',
});

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                // Don't retry 401 errors — the axios interceptor handles token refresh retries.
                // Retrying at the query level just compounds the problem during session refresh.
                if (error && typeof error === 'object' && 'response' in error) {
                    const axiosError = error as { response?: { status?: number } };
                    if (axiosError.response?.status === 401) {
                        return false;
                    }
                }
                return failureCount < 3;
            },
        },
    },
});

export function App() {
    const [selectedTheme, setSelectedTheme] = useState<'LIGHT' | 'DARK'>(() => {
        const localStorageItem = localStorage.getItem('GLUE_THEME');
        if (localStorageItem === 'LIGHT' || localStorageItem === 'DARK') {
            return localStorageItem;
        }
        return 'LIGHT';
    });
    const themeSwitchingContextValue = useMemo(() => {
        return {
            activeTheme: selectedTheme,
            toggleTheme: () => {
                switch (selectedTheme) {
                    case 'LIGHT':
                        setSelectedTheme('DARK');
                        localStorage.setItem('GLUE_THEME', 'DARK');
                        break;
                    case 'DARK':
                        setSelectedTheme('LIGHT');
                        localStorage.setItem('GLUE_THEME', 'LIGHT');
                        break;
                }
            },
        };
    }, [selectedTheme]);

    const currentTheme = selectedTheme === 'LIGHT' ? LightTheme : DarkTheme;

    return (
        <>
            <QueryClientProvider client={queryClient}>
                <ThemeSwitchingContext.Provider value={themeSwitchingContextValue}>
                    <ThemeProvider theme={currentTheme}>
                        <ThemeManager theme={currentTheme} />
                        <GlobalStyle />
                        <RouterProvider router={router} />
                        <Toaster position="bottom-right" />
                    </ThemeProvider>
                </ThemeSwitchingContext.Provider>
            </QueryClientProvider>
        </>
    );
}
